/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useState } from 'react';
import {
  Chip,
  Divider,
  Drawer,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Progress } from '@backstage/core-components';
import { RouteCoverageTable } from './RouteCoverageTable';
import {
  AuthzEnvState,
  AuthzServiceNode,
  COLUMN_LABEL,
  columnFor,
} from './types';
import { useAuthorizationPolicyApi } from './useAuthorizationPolicyApi';

const useStyles = makeStyles(theme => ({
  paper: { width: 720, padding: theme.spacing(2) },
  head: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  section: { marginTop: theme.spacing(2) },
  envHead: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  path: { fontFamily: 'monospace', fontSize: '0.75rem' },
}));

const EnvSection = ({ env }: { env: AuthzEnvState }) => {
  const classes = useStyles();
  const hasMgmt = env.management.length > 0;
  return (
    <div>
      <div className={classes.envHead}>
        <Typography variant="subtitle1">{env.env}</Typography>
        <Chip size="small" label={COLUMN_LABEL[columnFor(env)]} variant="outlined" />
        {env.chartRev && (
          <Chip size="small" label={env.chartRev} variant="outlined" />
        )}
        <Chip size="small" label={`port ${env.port}`} variant="outlined" />
      </div>
      <Typography variant="caption" color="textSecondary" className={classes.path}>
        {env.path}
      </Typography>

      {env.parseError && (
        <Alert severity="error" className={classes.section}>
          {env.parseError}
        </Alert>
      )}

      {env.blockers.length > 0 && (
        <Alert
          severity={columnFor(env) === 'notRolledOut' ? 'info' : 'warning'}
          className={classes.section}
        >
          <AlertTitle>
            {columnFor(env) === 'notRolledOut'
              ? 'Not rolled out yet'
              : 'Cannot start the soak yet'}
          </AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {env.blockers.map(b => (
              <li key={b.code}>{b.detail}</li>
            ))}
          </ul>
        </Alert>
      )}

      {env.phase2Blockers.length > 0 && (
        <Alert
          severity={env.renderErrors.length > 0 ? 'error' : 'warning'}
          className={classes.section}
        >
          <AlertTitle>
            {env.renderErrors.length > 0
              ? 'Would fail to render'
              : 'Blocks the Phase 2 cutover'}
          </AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {env.phase2Blockers.map((b, i) => (
              <li key={`${b.code}-${i}`}>{b.detail}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className={classes.section}>
        <RouteCoverageTable env={env} />
      </div>

      {hasMgmt && (
        <Alert severity="warning" className={classes.section}>
          <AlertTitle>Management callers may not be bindable here</AlertTitle>
          A <code>forMgmt</code> path is edge-exposed by construction: the chart
          maps it to the meshed <code>mgmt</code> ingress, so a caller arriving
          that way presents <b>Traefik's</b> ServiceAccount, not its own.{' '}
          <code>managementCallers</code> only binds workloads that dial the pod
          directly in-cluster. Do not add Traefik's ServiceAccount to
          compensate, that authorizes everything Traefik fronts. The app-layer
          allowlist (PLAT-938) reads the same identity, so it sees the same
          substitution.
        </Alert>
      )}
    </div>
  );
};

/**
 * Per-service detail. Read-only for now: this surface exists to make route
 * coverage and phase readiness legible before anyone can enable the policy,
 * so there is deliberately no edit path yet (PLAT-1261).
 */
export const AuthorizationDetailDrawer = ({
  service,
  onClose,
}: {
  service: string | null;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const api = useAuthorizationPolicyApi();
  const [node, setNode] = useState<AuthzServiceNode | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!service) {
      setNode(null);
      return undefined;
    }
    let active = true;
    setNode(null);
    setError(null);
    api
      .getService(service)
      .then(n => active && setNode(n))
      .catch(e => active && setError((e as Error).message));
    return () => {
      active = false;
    };
  }, [service, api]);

  return (
    <Drawer
      anchor="right"
      open={!!service}
      onClose={onClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.head}>
        <Typography variant="h6" style={{ flexGrow: 1 }}>
          {service}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />
      {error && <Alert severity="error">{error}</Alert>}
      {!node && !error && <Progress />}
      {node?.envs.length === 0 && (
        <Alert severity="warning" className={classes.section}>
          No environment values files were read for this service on the last
          rebuild.
        </Alert>
      )}
      {node?.envs.map(env => (
        <EnvSection key={env.env} env={env} />
      ))}
    </Drawer>
  );
};
